/**
 * Чистая логика виджета истории расчётов по карточке товара — без antd/react.
 *
 * Отдельный модуль по той же причине, что `widgets/calculator/productCardApply.ts`:
 * рендер (`Card`/`List`/`Popconfirm`, `useAppColors`, `react-i18next`) слишком тяжёл
 * для юнит-теста, а решающие правила ниже — чистые функции, которые проверяются
 * напрямую.
 *
 * Кнопка удаления показывается КАЖДОЙ записи, не только своей: список отдаёт
 * `owner_name`, а не `owner_id` (см. `ProductCalculationListItem` в
 * `shared/api/client.ts`) — потому что список видят все, у кого есть доступ к
 * товару, а `owner_id` бэкенд возвращает только в детали одной записи. Сопоставить
 * `owner_name` с идентификатором текущего пользователя нечем: это разные поля, и
 * гадать по совпадению строк значило бы выдумывать данные, которых нет в ответе
 * (`no-fallbacks.md`). Поэтому фактическое право проверяет бэкенд: и чужое удаление,
 * и чужая перезапись отвечают 403, а `classifyOwnershipFailure` даёт для этого
 * отдельное сообщение вместо общего «не удалось».
 */

export type ProductHistoryListState = 'loading' | 'error' | 'empty' | 'ready';

/** Приоритет состояний: идёт загрузка → ошибка загрузки → пусто → есть записи. */
export function deriveListState(
  loading: boolean,
  hasError: boolean,
  itemCount: number,
): ProductHistoryListState {
  if (loading) return 'loading';
  if (hasError) return 'error';
  if (itemCount === 0) return 'empty';
  return 'ready';
}

/** Пустое (после обрезки пробелов) название не отправляется — бэкенд его отвергнет. */
export function isSaveTitleValid(title: string): boolean {
  return title.trim().length > 0;
}

export type OwnershipFailureReason = 'forbidden' | 'missing' | 'generic';

/**
 * HTTP-статус отказа удаления/перезаписи → смысловая причина, а не общая ошибка.
 *
 * 404 в этих ветках значит ровно «записи больше нет»: её удалили из другой
 * вкладки или другим пользователем с доступом к товару. Под общим «не удалось
 * сохранить» пользователь жмёт «Сохранить» ещё раз и получает то же самое,
 * потому что перезаписывать нечего — правильный ход другой: сохранить заново.
 */
export function classifyOwnershipFailure(status: number | undefined): OwnershipFailureReason {
  if (status === 403) return 'forbidden';
  if (status === 404) return 'missing';
  return 'generic';
}

/** Запись истории, из которой сейчас загружен расчёт в калькуляторе. */
export interface LoadedRecord {
  id: number;
  title: string;
}

export type SaveAction = { kind: 'create' } | { kind: 'overwrite'; id: number };

/**
 * Реши, создаёт сохранение новую запись истории или перезаписывает загруженную.
 *
 * Перезапись — только когда сохраняемое название совпадает (после `trim()`) с
 * названием записи, из которой пришёл текущий расчёт. Если пользователь название
 * поменял, это уже другой расчёт по смыслу — создаём новую запись, а не стираем
 * молча исходную под новым именем.
 */
export function decideSaveAction(loadedRecord: LoadedRecord | null, title: string): SaveAction {
  if (loadedRecord && loadedRecord.title.trim() === title.trim()) {
    return { kind: 'overwrite', id: loadedRecord.id };
  }
  return { kind: 'create' };
}
